// review-chrome — the teacher review surface host chrome (clientside-teacher-review,
// tasks 3.4 + 3.6). Wires the feedback lifecycle over the existing course endpoints
// (editor-api): Send (sendFeedback), debounced comment autosave (autosaveFeedbackComments),
// option autosave (autosaveSubmissionOpt), the submittedLate Accept action
// (acceptSubmission), and in-page Prev/Next/Back student navigation. The teacher's code
// edits stay ephemeral in the editor-core buffer until Send.
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
// editor-core is already constructed (editable) and loaded by main.js.

// assignment-success-criteria ([4.3]): the shared success-criteria drawer, rendered
// READ-ONLY here so the teacher sees the criteria + the student's own self-ticks
// alongside the submitted work (no clicking, no save note).
import { wireSuccessCriteriaDrawer } from './success-criteria-drawer.js';

var COMMENT_DEBOUNCE_MS = 500;

// --- code-authenticity step-through (code-authenticity-snapshots, task 5.3) ----------
// Read-only walk over the flagged pastes. For each paste it renders the STORED
// historical full-file snapshot saved on the record (NOT editor-core's live draft,
// which would show the student's current code — design D5), with the pasted region
// highlighted in place, plus Prev/Next paging. Self-contained: it does not touch
// editor-core. Inert when the payload block is absent (flag off / no flagged pastes).

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Convert an Ace-style {row, column} position into a character offset into `text`.
// Rows are 0-based lines; clamps defensively so a stale/odd location never throws.
function offsetOf(text, row, column) {
  var lines = text.split('\n');
  var r = Math.max(0, Math.min(row || 0, lines.length));
  var off = 0;
  for (var i = 0; i < r; i++) { off += lines[i].length + 1; } // +1 for the '\n'
  off += Math.max(0, column || 0);
  return Math.min(off, text.length);
}

// Render one stored file with the pasted [start,end] range wrapped in a highlight span.
// Falls back to plain escaped code when the range is missing/invalid.
function renderHighlighted(content, loc) {
  var text = typeof content === 'string' ? content : '';
  if (!loc) { return escapeHtml(text); }
  var start = offsetOf(text, loc.startRow, loc.startColumn);
  var end = offsetOf(text, loc.endRow, loc.endColumn);
  if (!(end > start)) { return escapeHtml(text); }
  return escapeHtml(text.slice(0, start)) +
    '<span class="paste-hit">' + escapeHtml(text.slice(start, end)) + '</span>' +
    escapeHtml(text.slice(end));
}

// Pick the file the paste landed in from the stored AFTER snapshot; fall back to the
// first file, then to the pasted text itself if no snapshot was stored.
function fileForPaste(paste) {
  var files = Array.isArray(paste.after) ? paste.after : [];
  var name = paste.location && paste.location.file;
  var match = null;
  for (var i = 0; i < files.length; i++) {
    if (files[i] && files[i].name === name) { match = files[i]; break; }
  }
  if (!match && files.length) { match = files[0]; }
  if (match) { return { name: match.name, content: match.content }; }
  return { name: name || '(pasted text)', content: paste.pastedText || '' };
}

function wireAuthenticity(document) {
  var loadEl = document.getElementById('review-authenticity-load');
  if (!loadEl || !loadEl.textContent) { return; }

  var pastes;
  try {
    var parsed = JSON.parse(loadEl.textContent);
    pastes = Array.isArray(parsed.pastes) ? parsed.pastes : [];
  } catch (e) { console.error(e); return; }
  if (!pastes.length) { return; }

  var prevBtn = document.getElementById('review-authenticity-prev');
  var nextBtn = document.getElementById('review-authenticity-next');
  var posEl = document.getElementById('review-authenticity-pos');
  var metaEl = document.getElementById('review-authenticity-meta');
  var fileEl = document.getElementById('review-authenticity-file');
  var codeEl = document.getElementById('review-authenticity-code');
  if (!codeEl) { return; }

  var idx = 0;

  function render() {
    var paste = pastes[idx];
    var file = fileForPaste(paste);
    if (posEl) { posEl.textContent = 'Paste ' + (idx + 1) + ' of ' + pastes.length; }
    if (metaEl) {
      var when = paste.created ? new Date(paste.created).toLocaleString() : '';
      metaEl.textContent = (paste.lineCount || 0) + ' line' + ((paste.lineCount === 1) ? '' : 's') +
        ' / ' + (paste.charCount || 0) + ' chars' + (when ? ' — ' + when : '') +
        (paste.kind === 'largeInsert' ? ' (large insert)' : '');
    }
    if (fileEl) { fileEl.textContent = file.name; }
    codeEl.innerHTML = renderHighlighted(file.content, paste.location);
    if (prevBtn) { prevBtn.disabled = idx <= 0; }
    if (nextBtn) { nextBtn.disabled = idx >= pastes.length - 1; }
  }

  if (prevBtn) { prevBtn.addEventListener('click', function () { if (idx > 0) { idx--; render(); } }); }
  if (nextBtn) { nextBtn.addEventListener('click', function () { if (idx < pastes.length - 1) { idx++; render(); } }); }
  render();
}

export function wire(ctx) {
  var document = ctx.doc;
  var core = ctx.core;
  var api = ctx.editorApi;
  var body = document.body;

  var submissionId = body.getAttribute('data-submission-id') || '';
  var courseCtx = {
    courseId: body.getAttribute('data-course-id') || '',
    lessonId: body.getAttribute('data-lesson-id') || '',
    materialId: body.getAttribute('data-material-id') || ''
  };
  var returnUrl = body.getAttribute('data-return-url') || '';
  var state = body.getAttribute('data-submission-state') || 'submitted';

  // assignment-success-criteria ([4.3]): render the success-criteria drawer READ-ONLY
  // — the criteria + the student's saved self-ticks (delivered via the review payload)
  // shown beside the submitted project. Disabled rows, no save note; hidden when the
  // assignment carries no criteria.
  var reviewPayload = ctx.payload || {};
  wireSuccessCriteriaDrawer({
    doc: document,
    criteria: reviewPayload.successCriteria,
    ticks: reviewPayload.checklistTicks,
    readOnly: true
  });

  var formBlock = document.getElementById('review-form-block');
  var acceptBlock = document.getElementById('review-accept-block');
  var completedBlock = document.getElementById('review-completed-block');
  var inProgressBlock = document.getElementById('review-inprogress-block');
  var commentsEl = document.getElementById('review-comments');
  var includeRevisionEl = document.getElementById('review-include-revision');
  var allowResubmitEl = document.getElementById('review-allow-resubmit');
  var sendBtn = document.getElementById('review-send');
  var acceptBtn = document.getElementById('review-accept');
  var editBtn = document.getElementById('review-edit');
  var statusEl = document.getElementById('review-status');
  var acceptStatusEl = document.getElementById('review-accept-status');
  var prevBtn = document.getElementById('review-prev');
  var nextBtn = document.getElementById('review-next');
  var backBtn = document.getElementById('review-back');
  // Send-a-suggestion control (teacher-code-suggestions) — present in every lifecycle state.
  var suggestNoteEl = document.getElementById('review-suggest-note');
  var suggestSendBtn = document.getElementById('review-suggest-send');
  var suggestStatusEl = document.getElementById('review-suggest-status');

  function setStatus(el, text, isError) {
    if (!el) { return; }
    el.textContent = text || '';
    el.classList.toggle('runner-error', !!isError);
  }
  function show(el, on) { if (el) { el.hidden = !on; } }

  // Render the chrome appropriate to the lifecycle state (submitted/editing form,
  // submittedLate Accept, completed read-only+Edit).
  function showState(s) {
    state = s;
    show(formBlock, s === 'submitted' || s === 'editing');
    show(acceptBlock, s === 'submittedLate');
    show(completedBlock, s === 'completed');
    // started / modified: in-progress work, viewable but not yet reviewable.
    show(inProgressBlock, s === 'started' || s === 'modified');
  }

  // --- comment + option autosave -------------------------------------------------
  var saveTimer = null;
  if (commentsEl) {
    commentsEl.addEventListener('input', function () {
      if (saveTimer) { clearTimeout(saveTimer); }
      saveTimer = setTimeout(function () {
        api.autosaveFeedbackComments(submissionId, commentsEl.value)
          .catch(function (err) { console.error(err); });
      }, COMMENT_DEBOUNCE_MS);
    });
  }
  function optChange() {
    api.autosaveSubmissionOpt(submissionId, {
      includeRevision: !!(includeRevisionEl && includeRevisionEl.checked),
      allowResubmit: !!(allowResubmitEl && allowResubmitEl.checked)
    }).catch(function (err) { console.error(err); });
  }
  if (includeRevisionEl) { includeRevisionEl.addEventListener('change', optChange); }
  if (allowResubmitEl) { allowResubmitEl.addEventListener('change', optChange); }

  // --- in-page student navigation ------------------------------------------------
  var ordered = [];
  var idx = -1;

  function dashboardUrl() {
    if (returnUrl) { return returnUrl; }
    return courseCtx.courseId ? ('/library/courses/' + encodeURIComponent(courseCtx.courseId)) : '/library/courses';
  }
  function navTo(sub) {
    if (!sub || !sub.lang || !sub.shortCode) { return; }
    var q = '?courseId=' + encodeURIComponent(courseCtx.courseId) +
      '&lessonId=' + encodeURIComponent(courseCtx.lessonId) +
      '&materialId=' + encodeURIComponent(courseCtx.materialId) +
      '&return=' + encodeURIComponent(returnUrl);
    window.location.href = '/assignment-embed-feedback/' + sub.lang + '/' + sub.shortCode + q;
  }
  function goNext() {
    if (idx >= 0 && idx + 1 < ordered.length) { navTo(ordered[idx + 1]); }
    else { window.location.href = dashboardUrl(); } // end of cohort -> back to dashboard
  }
  if (backBtn) {
    backBtn.addEventListener('click', function () { window.location.href = dashboardUrl(); });
  }

  // --- Send / Accept / Edit ------------------------------------------------------
  if (sendBtn) {
    sendBtn.addEventListener('click', function () {
      sendBtn.disabled = true;
      setStatus(statusEl, 'Sending…', false);
      api.sendFeedback(courseCtx, submissionId, core.getFiles(), {
        comments: commentsEl ? commentsEl.value : '',
        includeRevision: !!(includeRevisionEl && includeRevisionEl.checked),
        allowResubmit: !!(allowResubmitEl && allowResubmitEl.checked)
      }).then(function () {
        setStatus(statusEl, 'Sent', false);
        goNext(); // advance to the next student (or back to the dashboard at the end)
      }).catch(function (err) {
        setStatus(statusEl, 'Send failed', true);
        sendBtn.disabled = false;
        console.error(err);
      });
    });
  }
  if (acceptBtn) {
    acceptBtn.addEventListener('click', function () {
      acceptBtn.disabled = true;
      setStatus(acceptStatusEl, 'Accepting…', false);
      api.acceptSubmission(courseCtx, submissionId).then(function () {
        showState('submitted'); // late submission accepted -> feedback form enabled
      }).catch(function (err) {
        setStatus(acceptStatusEl, 'Accept failed', true);
        acceptBtn.disabled = false;
        console.error(err);
      });
    });
  }
  if (editBtn) {
    editBtn.addEventListener('click', function () { showState('editing'); });
  }

  // --- Send suggestion (teacher-code-suggestions) --------------------------------
  // Formative coaching, decoupled from grading and available in ANY lifecycle state:
  // snapshots the teacher's LIVE editor buffer (getFiles()) + a note onto the student's
  // working trinket. Does not advance the student nav or change the submission state.
  if (suggestSendBtn) {
    suggestSendBtn.addEventListener('click', function () {
      suggestSendBtn.disabled = true;
      setStatus(suggestStatusEl, 'Sending…', false);
      api.sendSuggestion(courseCtx, submissionId, core.getFiles(), suggestNoteEl ? suggestNoteEl.value : '')
        .then(function () {
          setStatus(suggestStatusEl, 'Suggestion sent', false);
          if (suggestNoteEl) { suggestNoteEl.value = ''; }
          suggestSendBtn.disabled = false;
        })
        .catch(function (err) {
          setStatus(suggestStatusEl, 'Send failed', true);
          suggestSendBtn.disabled = false;
          console.error(err);
        });
    });
  }

  // Fetch the cohort's ordered submissions so Prev/Next can navigate within the
  // isolated surface; disable the controls if the context/list is unavailable.
  function disableNav() {
    if (prevBtn) { prevBtn.disabled = true; }
    if (nextBtn) { nextBtn.disabled = true; }
  }
  if (courseCtx.courseId && courseCtx.materialId) {
    api.fetchSubmissions(courseCtx).then(function (list) {
      var rows = Array.isArray(list) ? list : ((list && list.data) || []);
      ordered = rows.filter(function (s) { return s && s.shortCode; });
      idx = ordered.findIndex(function (s) {
        return s.id === submissionId || s.trinketId === submissionId || s.shortCode === submissionId;
      });
      if (prevBtn) {
        prevBtn.disabled = !(idx > 0);
        prevBtn.addEventListener('click', function () { if (idx > 0) { navTo(ordered[idx - 1]); } });
      }
      if (nextBtn) {
        nextBtn.disabled = !(idx >= 0 && idx + 1 < ordered.length);
        nextBtn.addEventListener('click', function () { if (idx >= 0 && idx + 1 < ordered.length) { navTo(ordered[idx + 1]); } });
      }
    }).catch(function (err) { disableNav(); console.error(err); });
  } else {
    disableNav();
  }

  // Code-authenticity step-through (read-only; renders stored snapshots, not the live
  // editor). Inert when the payload block is absent (flag off / no flagged pastes).
  wireAuthenticity(document);

  showState(state);
}
